import React, { createContext, useContext, ReactNode } from "react";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useUser } from "@/context/UserContext";
import { filter, isEmpty, isNull, isNil, uniqBy } from "lodash";
import { getGuestPlaysToday as helperGuestPlayTodays, getStartAndEndOfWeek } from "@/helpers/helpers";

interface GuestPlay {
    created_at: string;
    score: number;
    area_id: string;
    id?: string;
    address?: string;
    guess?: string | number;
    isChallengeGuess?: boolean;
    challenge_id?: string;
}

interface GuestContextType {
    guestPlay: GuestPlay[];
    setGuestPlay: (value: GuestPlay[]) => void;
    getGuestPlaysToday: () => GuestPlay[];
    getGuestPlaysWeek: (date?: Date, areaScope?: "local" | "regional" | "all") => GuestPlay[];
    totalGuestPlayScoreDaily: () => number;
    totalGuestPlayScoreWeek: (date?: Date, areaScope?: "local" | "regional" | "all") => number;
}

const GuestContext = createContext<GuestContextType | undefined>(undefined);

interface GuestProviderProps {
    children: ReactNode;
}

export const GuestProvider = ({ children }: GuestProviderProps) => {
    const [guestPlay, setGuestPlay] = useLocalStorage<GuestPlay[]>("guest_gameplay", []);
    const { userState } = useUser();

    /**
     * Get the guest plays that occurred today for the current user.
     *
     * This function filters the guest play data to find plays that occurred today
     * in the same area as the current user.
     *
     * @returns An array of guest play records that occurred today in the user's current area.
     */
    const getGuestPlaysToday = (): GuestPlay[] => {
        // Always read from localStorage directly to ensure we have the latest data
        // This avoids stale state issues when setGuestPlay hasn't updated the state yet
        const storedGuestPlay = JSON.parse(localStorage.getItem("guest_gameplay") || "[]") as GuestPlay[];
        return helperGuestPlayTodays(storedGuestPlay, userState);
    };

    /**
     * Get the guest plays that occurred within the current week for the user's current area.
     *
     * This function filters the guest play data to find plays that occurred within the current week
     * and in the same area as the current user.
     *
     * @param date - The date to use for calculating the week. Defaults to the current date.
     * @param areaScope - The area scope to filter guest plays. Defaults to "local".
     *
     * @returns An array of guest play records that occurred within the current week in the user's current area.
     */
    const getGuestPlaysWeek = (
        date: Date = new Date(),
        areaScope: "local" | "regional" | "all" = "local",
    ): GuestPlay[] => {
        const currentDate = new Date(date);

        return filter(guestPlay, (gameplay) => {
            const gameplayDate = new Date(gameplay.created_at);
            const weekData = getStartAndEndOfWeek(gameplayDate.toLocaleDateString()) as any;
            const { startOfWeek, endOfWeek } = weekData;
            const isWithinWeek = currentDate >= new Date(startOfWeek) && currentDate <= new Date(endOfWeek);

            // The default filter should be 'local', the gameplay area is equal to the user's current area
            let isSameArea = gameplay.area_id === userState?.currentAreaId;

            // TODO: Add filter for regional areas

            // If the filter is 'all', just make sure that the gameplay has an area
            if (areaScope === "all") {
                isSameArea = !isEmpty(gameplay.area_id) && !isNil(gameplay?.area_id) && !isNull(gameplay.area_id);
            }

            return isWithinWeek && isSameArea;
        });
    };

    /**
     * Calculates the total score from guest plays for the current day in a specified area scope.
     *
     * @returns The total guest play score for the day in the specified area scope.
     */
    const totalGuestPlayScoreDaily = (): number => {
        const todayPlays = getGuestPlaysToday();
        return uniqBy(todayPlays, "id")?.reduce((prev, curr) => prev + curr.score, 0) ?? 0;
    };

    /**
     * Calculates the total score from guest plays for the current week in a specified area scope.
     *
     * @param date - The date for which to calculate the total guest play score.
     * @param areaScope - The area scope for which to calculate the total guest play score.
     *
     * @returns The total guest play score for the week in the specified area scope.
     */
    const totalGuestPlayScoreWeek = (date?: Date, areaScope: "local" | "regional" | "all" = "local"): number => {
        const weekPlays = getGuestPlaysWeek(date, areaScope);
        return weekPlays?.reduce((prev, curr) => prev + curr.score, 0) ?? 0;
    };

    return (
        <GuestContext.Provider
            value={{
                guestPlay,
                setGuestPlay,
                getGuestPlaysToday,
                getGuestPlaysWeek,
                totalGuestPlayScoreDaily,
                totalGuestPlayScoreWeek,
            }}
        >
            {children}
        </GuestContext.Provider>
    );
};

export const useGuestContext = (): GuestContextType => {
    const context = useContext(GuestContext);
    if (context === undefined) {
        throw new Error("useGuestContext must be used within a GuestProvider");
    }
    return context;
};
